import { SupportedChain } from "@/hooks/get-tokens-and-chains";
import { ChainId, ChainKey, ChainType, Token } from "@lifi/sdk";
import { zeroAddress } from "viem";
import { create } from "zustand";
import { getToken } from "@/hooks/get-tokens-and-chains";
import { refreshEvmBalances } from "@/hooks/use-token-balances";

export interface IUserSettings {
  speed: string;
  slippage: string;
}

export const useTokenStore = create<{
  fromToken: Token;
  setFromToken: (token: Token) => void;
  fromTokenAmount: string;
  setFromTokenAmount: (amount: string) => void;
  fromTokenAmountUSD: string;
  setFromTokenAmountUSD: (amount: string) => void;
  toToken: Token;
  setToToken: (token: Token) => void;
  toTokenAmount: string;
  setToTokenAmount: (amount: string) => void;
  toTokenAmountUSD: string;
  setToTokenAmountUSD: (amount: string) => void;
  fromChain: SupportedChain;
  setFromChain: (chain: SupportedChain) => void;
  toChain: SupportedChain;
  setToChain: (chain: SupportedChain) => void;
  svmAddress: string | undefined;
  setSvmAddress: (address: string | undefined) => void;
  isValidSvmAddress: boolean | null;
  setIsValidSvmAddress: (isValid: boolean | null) => void;
  initializeSolPrice: () => Promise<void>;
}>((set) => ({
  fromToken: {
    chainId: ChainId.SOL,
    address: "11111111111111111111111111111111",
    symbol: "SOL",
    name: "Solana",
    decimals: 9,
    logoURI:
      "https://assets.coingecko.com/coins/images/4128/small/solana.png?1640133422",
    priceUSD: "0",
  },
  setFromToken: (token: Token) => set({ fromToken: token }),
  fromTokenAmount: "",
  setFromTokenAmount: (amount: string) => set({ fromTokenAmount: amount }),
  fromTokenAmountUSD: "0",
  setFromTokenAmountUSD: (amount: string) =>
    set({ fromTokenAmountUSD: amount }),
  toToken: {
    chainId: ChainId.SOL,
    address: "EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v",
    symbol: "USDC",
    name: "USD Coin",
    decimals: 6,
    logoURI:
      "https://assets.coingecko.com/coins/images/6319/small/USD_Coin_icon.png",
    priceUSD: "1",
  },
  setToToken: (token: Token) => set({ toToken: token }),
  toTokenAmount: "",
  setToTokenAmount: (amount: string) => set({ toTokenAmount: amount }),
  toTokenAmountUSD: "0",
  setToTokenAmountUSD: (amount: string) => set({ toTokenAmountUSD: amount }),
  fromChain: {
    id: ChainId.SOL,
    name: "Solana",
    logoURI:
      "https://raw.githubusercontent.com/lifinance/types/main/src/assets/icons/chains/solana.svg",
    chainType: ChainType.SVM,
    symbol: ChainKey.SOL,
  },
  setFromChain: (chain: SupportedChain) => {
    set({ fromChain: chain });
    // Add EVM chain to active set for lazy polling
    if (chain.chainType === ChainType.EVM && typeof chain.id === 'number') {
      useActiveEvmChainsStore.getState().addChain(chain.id);
    }
  },
  toChain: {
    id: ChainId.SOL,
    name: "Solana",
    logoURI:
      "https://raw.githubusercontent.com/lifinance/types/main/src/assets/icons/chains/solana.svg",
    chainType: ChainType.SVM,
    symbol: ChainKey.SOL,
  },
  setToChain: (chain: SupportedChain) => {
    set({ toChain: chain });
    // Add EVM chain to active set for lazy polling
    if (chain.chainType === ChainType.EVM && typeof chain.id === 'number') {
      useActiveEvmChainsStore.getState().addChain(chain.id);
    }
  },
  svmAddress: undefined,
  setSvmAddress: (address: string | undefined) => set({ svmAddress: address }),
  isValidSvmAddress: null,
  setIsValidSvmAddress: (isValid: boolean | null) =>
    set({ isValidSvmAddress: isValid }),
  initializeSolPrice: async () => {
    try {
      const solToken = await getToken(ChainId.SOL, "11111111111111111111111111111111");
      if (solToken && solToken.priceUSD && solToken.priceUSD !== "0") {
        set({ fromToken: solToken });
      }
    } catch (error) {
      console.warn("Failed to fetch SOL price on initialization:", error);
    }
  },
}));

export const useUserSettingsStore = create<{
  userSettings: IUserSettings;
  setUserSettings: (settings: IUserSettings) => void;
}>((set) => ({
  userSettings: {
    speed: "default",
    slippage: "0.5",
  },
  setUserSettings: (settings: IUserSettings) => set({ userSettings: settings }),
}));

// Shared wallet state - isEmailOrSocialWallet only goes true, never back to false
// This prevents logout from firing when wallet object temporarily loses email property during switches
export const useWalletStore = create<{
  isEmailOrSocialWallet: boolean;
  setIsEmailOrSocialWallet: (value: boolean) => void;
}>((set, get) => ({
  isEmailOrSocialWallet: false,
  // Only update to true, never back to false (sticky behavior)
  setIsEmailOrSocialWallet: (value: boolean) => {
    if (value && !get().isEmailOrSocialWallet) {
      set({ isEmailOrSocialWallet: true });
    }
  },
}));

// Active EVM chains for lazy polling - only poll chains user has interacted with
// Default: ETH + BNB + Base. Other chains added when user selects them in swap UI.
// This prevents polling ALL 15 configured chains when user only uses 1-3.
export const useActiveEvmChainsStore = create<{
  activeChains: Set<number>;
  addChain: (chainId: number) => void;
  isChainActive: (chainId: number) => boolean;
}>((set, get) => ({
  // Default: ETH (1), BNB (56), Base (8453) - highest user adoption
  activeChains: new Set([1, 56, 8453]),
  addChain: (chainId: number) => {
    const current = get().activeChains;
    if (!current.has(chainId)) {
      set({ activeChains: new Set([...current, chainId]) });
      // Trigger immediate balance fetch for newly activated chain
      refreshEvmBalances();
    }
  },
  isChainActive: (chainId: number) => get().activeChains.has(chainId),
}));

export const useCreditCardBuyStore = create<{
  token: Token;
  setToken: (token: Token) => void;
  selectedOnRampChainId: number | null;
  setSelectedOnRampChainId: (chainId: number | null) => void;
  isWertWidgetOpen: boolean;
  setIsWertWidgetOpen: (isOpen: boolean) => void;
  isLoadingSwap: boolean;
  setIsLoadingSwap: (isLoading: boolean) => void;
}>((set) => ({
  token: {
    chainId: 1,
    address: zeroAddress,
    symbol: "ETH",
    name: "Ethereum",
    decimals: 18,
    logoURI: "/icons/chains/ethereum.png",
    priceUSD: "0",
  },
  setToken: (token: Token) => set({ token }),
  selectedOnRampChainId: null,
  setSelectedOnRampChainId: (chainId: number | null) =>
    set({ selectedOnRampChainId: chainId }),
  isWertWidgetOpen: false,
  setIsWertWidgetOpen: (isOpen: boolean) => set({ isWertWidgetOpen: isOpen }),
  isLoadingSwap: false,
  setIsLoadingSwap: (isLoading: boolean) => set({ isLoadingSwap: isLoading }),
}));
